import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Tag, ShieldCheck, ArrowRight } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

const STEPS = ["Service", "Checkout", "Payment", "Dashboard"];

export default function Checkout() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [item, setItem] = useState(null);
  const [service, setService] = useState(null);
  const [networks, setNetworks] = useState([]);
  const [form, setForm] = useState({ package_name: null, quantity: 1, network: "TRC20", coupon_code: "", notes: "" });
  const [discount, setDiscount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem("adnexa_checkout");
    if (!stored) { nav("/services"); return; }
    const parsed = JSON.parse(stored);
    setItem(parsed);
    setForm((f) => ({ ...f, package_name: parsed.package_name }));
    api.get("/payment-networks").then((r) => { setNetworks(r.data); if (r.data[0]) setForm((f) => ({ ...f, network: r.data[0].network })); }).catch(() => {});
    // fetch full service by id via services list
    api.get("/services").then((r) => {
      const s = r.data.find((x) => x.id === parsed.service_id);
      setService(s);
    }).catch(() => {});
  }, [nav]);

  const unitPrice = (() => {
    if (!service) return item?.price || 0;
    const pkg = service.packages?.find((p) => p.name === form.package_name);
    return pkg ? pkg.price : service.price;
  })();
  const subtotal = +(unitPrice * form.quantity).toFixed(2);
  const total = +Math.max(0, subtotal - discount).toFixed(2);

  const applyCoupon = async () => {
    if (!form.coupon_code) return;
    try { const { data } = await api.post("/coupons/validate", { code: form.coupon_code, subtotal }); setDiscount(data.discount); toast.success(`Coupon applied: -${data.discount} USDT`); }
    catch (e) { setDiscount(0); toast.error(apiError(e)); }
  };

  const placeOrder = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", {
        service_id: item.service_id, package_name: form.package_name, quantity: form.quantity,
        network: form.network, coupon_code: form.coupon_code || null, notes: form.notes,
      });
      sessionStorage.removeItem("adnexa_checkout");
      toast.success("Order created! Complete your payment.");
      nav(`/payment/${data.order.order_id}`);
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  };

  if (!item) return <div className="flex justify-center py-40"><Loader2 className="animate-spin text-amber-400" size={40} /></div>;

  return (
    <div className="section-lux">
      <div className="container-lux max-w-5xl">
        {/* stepper */}
        <div className="flex items-center justify-center gap-2 sm:gap-4 mb-12">
          {STEPS.map((s, i) => (
            <div key={s} className="flex items-center gap-2 sm:gap-4">
              <div className={`flex items-center gap-2 ${i <= 1 ? "text-amber-400" : "text-slate-600"}`}>
                <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold ${i <= 1 ? "bg-gradient-to-r from-amber-400 to-amber-600 text-black" : "border border-slate-700"}`}>{i + 1}</span>
                <span className="text-xs sm:text-sm font-medium hidden sm:inline">{s}</span>
              </div>
              {i < STEPS.length - 1 && <div className="h-px w-6 sm:w-12 bg-slate-700" />}
            </div>
          ))}
        </div>

        <h1 className="text-3xl font-extrabold text-white mb-8">{t("checkout.title")}</h1>
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="card-lux p-6">
              <h3 className="font-semibold text-white mb-4">{item.service_name}</h3>
              {service?.packages?.length > 0 && (
                <div className="space-y-3">
                  <label className="label-lux">{t("checkout.package")}</label>
                  {service.packages.map((p) => (
                    <button key={p.name} onClick={() => setForm({ ...form, package_name: p.name })} data-testid={`checkout-package-${p.name}`}
                      className={`w-full text-left rounded-lg border p-4 transition-all ${form.package_name === p.name ? "border-amber-500 bg-amber-500/5" : "border-white/10 hover:border-amber-500/40"}`}>
                      <div className="flex justify-between"><span className="font-medium text-white">{p.name}</span><span className="font-bold text-amber-400">{p.price} USDT</span></div>
                      {p.description && <p className="text-xs text-slate-400 mt-1">{p.description}</p>}
                    </button>
                  ))}
                </div>
              )}
              <div className="mt-4">
                <label className="label-lux">{t("checkout.quantity")}</label>
                <input type="number" min="1" className="input-lux mt-2 w-32" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Math.max(1, +e.target.value) })} data-testid="checkout-quantity-input" />
              </div>
            </div>

            <div className="card-lux p-6">
              <label className="label-lux">{t("checkout.network")}</label>
              <div className="grid grid-cols-3 gap-3 mt-3">
                {networks.map((n) => (
                  <button key={n.network} onClick={() => setForm({ ...form, network: n.network })} data-testid={`checkout-network-${n.network}`}
                    className={`rounded-lg border p-3 text-center transition-all ${form.network === n.network ? "border-amber-500 bg-amber-500/5 text-white" : "border-white/10 text-slate-400 hover:border-amber-500/40"}`}>
                    <div className="font-semibold text-sm">USDT</div><div className="text-xs">{n.network}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="card-lux p-6">
              <label className="label-lux">{t("checkout.notes")}</label>
              <textarea rows={3} className="input-lux mt-2 resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="checkout-notes-input" />
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="card-lux p-6 sticky top-24" data-testid="checkout-summary">
              <h3 className="font-semibold text-white mb-4">{t("checkout.summary")}</h3>
              <div className="flex gap-2 mb-4">
                <input placeholder={t("checkout.coupon")} className="input-lux" value={form.coupon_code} onChange={(e) => setForm({ ...form, coupon_code: e.target.value })} data-testid="checkout-coupon-input" />
                <button onClick={applyCoupon} className="silver-btn !px-4 text-sm" data-testid="checkout-apply-coupon"><Tag size={15} /></button>
              </div>
              <div className="space-y-2.5 text-sm border-t border-white/5 pt-4">
                <div className="flex justify-between text-slate-400"><span>{t("checkout.subtotal")}</span><span className="text-white">{subtotal} USDT</span></div>
                {discount > 0 && <div className="flex justify-between text-emerald-400"><span>{t("checkout.discount")}</span><span>-{discount} USDT</span></div>}
                <div className="flex justify-between text-lg font-bold pt-2 border-t border-white/5"><span className="text-white">{t("checkout.total")}</span><span className="gold-text">{total} USDT</span></div>
              </div>
              <button disabled={loading} onClick={placeOrder} className="gold-btn w-full mt-5" data-testid="checkout-place-order-btn">
                {loading ? <Loader2 className="animate-spin" size={18} /> : <>{t("checkout.place")} <ArrowRight size={16} /></>}
              </button>
              <div className="flex items-center justify-center gap-2 mt-4 text-xs text-slate-500"><ShieldCheck size={14} className="text-amber-400" /> Secure USDT payment · Verified on-chain</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
